// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';
import { attemptChunkLoadRecovery, isDynamicChunkLoadError } from '@utils/chunkLoadRecovery';
import { I18nContext, type TranslationParams } from '@/contexts/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  public static contextType = I18nContext;
  declare context: React.ContextType<typeof I18nContext>;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    attemptChunkLoadRecovery(error);
    this.setState({ error, errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private translate(key: string, fallback: string, params?: TranslationParams) {
    const translator = this.context?.t;
    if (!translator) return fallback;
    if (params) return translator(key, fallback, params);
    return translator(key, fallback);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-6">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="mb-2 text-xl font-semibold">
            {this.translate('errorBoundary.title', 'Something went wrong')}
          </h2>
          <p className="mb-6 max-w-md text-center text-muted-foreground">
            {this.state.error && isDynamicChunkLoadError(this.state.error)
              ? this.translate(
                  'errorBoundary.chunkLoadHint',
                  'A new version is being loaded. If the page does not refresh automatically, click Reload Page.'
                )
              : this.translate(
                  'errorBoundary.genericHint',
                  'We apologize for the inconvenience. Please try refreshing the page or contact support if the problem persists.'
                )}
          </p>

          {this.state.error && (
            <div className="mb-6 w-full max-w-2xl overflow-auto rounded-lg bg-muted p-4">
              <p className="font-mono text-sm text-destructive">{this.state.error.toString()}</p>
              {this.state.errorInfo && (
                <pre className="mt-2 overflow-auto text-xs text-muted-foreground">
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={this.handleRetry} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              {this.translate('errorBoundary.tryAgain', 'Try Again')}
            </Button>
            <Button onClick={() => window.location.reload()}>
              {this.translate('errorBoundary.reloadPage', 'Reload Page')}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
